import React, { useEffect, useMemo, useState } from 'react';

import { apiGet } from '../api';
import { useTeamDashboard } from '../TeamDashboardContext';
import type { Project } from '../types';
import './reports.css';

type RepTab = 'daily' | 'budget' | 'efficiency' | 'complete';
type RepRange = 'week' | 'month' | 'sprint' | 'all';
type ReportEntryStatus = 'done' | 'pending' | 'risk';

type ReportEntry = {
  who: string;
  title: string;
  status: ReportEntryStatus;
  type: string;
  hrs: number;
  cost: number;
};

type ReportDay = {
  date: string;
  short: string;
  entries: ReportEntry[];
};

type ReportMember = {
  initials: string;
  name: string;
  role: string;
  rate: number;
  hrs: number;
  commits: number;
  prs: number;
  quality: number;
  color: string;
  spent: number;
};

type ProjectTimelineEntry = {
  day: string;
  who: string;
  text: string;
  color: string;
};

type ProjectReport = {
  id: string;
  name: string;
  color: string;
  budget: number;
  spent: number;
  complete: number;
  deadline: string;
  status: string;
  members: string[];
  tasks: {
    done: number;
    pending: number;
    total: number;
  };
  timeline: ProjectTimelineEntry[];
  topPerformer: {
    init: string;
    name: string;
    reason: string;
  };
};

type EfficiencyRow = {
  initials: string;
  name: string;
  role: string;
  quality: number;
  commits: number;
  prs: number;
  hours: number;
  hrsRatio: string;
  score: number;
  verdClass: string;
  verd: string;
  color: string;
  spent: number;
  hourlyRate: number;
  rank: number;
};

type InsightItem = {
  iconBg: string;
  iconBorder: string;
  iconColor: string;
  iconPath: React.ReactNode;
  text: React.ReactNode;
};

type LiveActivityItem = {
  id: string;
  memberInitials: string;
  memberName: string;
  action: string;
  detail: string;
  cost: number;
  type: string;
  timestamp: string;
};

type LiveCommitItem = {
  id: string;
  memberInitials: string;
  memberName: string;
  message: string;
  timestamp: string;
  linesAdded: number;
  linesRemoved: number;
  repo: string;
  branch: string;
};

type LiveQualityItem = {
  id: string;
  memberId: string;
  memberInitials: string;
  memberName: string;
  score: number;
  bugs: number;
  linesReviewed: number;
  date: string;
};

const REPORT_RANGE_LIMITS: Record<RepRange, number> = {
  week: 5,
  month: 7,
  sprint: 10,
  all: 30,
};

const REPORT_MEMBER_COLORS = ['#4d9cf8', '#3ecf8e', '#f26464', '#f5a623', '#8f7dff', '#36cfc9'];

const DEMO_REPORT_DAYS: ReportDay[] = [
  {
    date: 'Fri, Apr 25',
    short: 'Apr25',
    entries: [
      { who: 'AK', title: 'feat: rate limiting auth endpoints', status: 'done', type: 'commit', hrs: 2, cost: 1000 },
      { who: 'AK', title: 'Daily standup & sprint planning', status: 'done', type: 'log', hrs: 2, cost: 1000 },
      { who: 'SR', title: 'refactor: Modularise API service layer', status: 'pending', type: 'commit', hrs: 4, cost: 1600 },
      { who: 'SR', title: 'Lead standup & backlog grooming', status: 'done', type: 'log', hrs: 2, cost: 800 },
      { who: 'RM', title: 'fix: Partial auth bug (session not persisting)', status: 'risk', type: 'commit', hrs: 3, cost: 1500 },
      { who: 'PK', title: 'PR #14 – Payment gateway integration', status: 'done', type: 'pr', hrs: 2, cost: 1000 },
    ],
  },
  {
    date: 'Thu, Apr 24',
    short: 'Apr24',
    entries: [
      { who: 'AK', title: 'PR #17 – Refactor dashboard API layer', status: 'done', type: 'pr', hrs: 3, cost: 1500 },
      { who: 'AK', title: 'Reviewed PR #14 by Priya', status: 'done', type: 'review', hrs: 1, cost: 500 },
      { who: 'AK', title: 'Architecture planning – microservices migration', status: 'done', type: 'log', hrs: 2, cost: 1000 },
      { who: 'SR', title: 'PR #16 – API refactor (in review)', status: 'pending', type: 'pr', hrs: 6, cost: 2400 },
      { who: 'SR', title: 'Reviewed PR #15 – DB migrations', status: 'done', type: 'review', hrs: 2, cost: 800 },
      { who: 'RM', title: 'PR #13 – Auth module (3 review rounds)', status: 'risk', type: 'pr', hrs: 3, cost: 1500 },
      { who: 'RM', title: 'Debugging – auth token expiry', status: 'pending', type: 'log', hrs: 2, cost: 1000 },
      { who: 'PK', title: 'feat: Stripe webhook handler & retry logic', status: 'done', type: 'commit', hrs: 2, cost: 1000 },
    ],
  },
  {
    date: 'Mon, Apr 21',
    short: 'Apr21',
    entries: [
      { who: 'AK', title: 'fix: Race condition in job queue', status: 'done', type: 'commit', hrs: 2.5, cost: 1250 },
      { who: 'SR', title: 'chore: Update dependency versions', status: 'done', type: 'commit', hrs: 1.5, cost: 600 },
      { who: 'SR', title: '1-on-1s with team members', status: 'done', type: 'log', hrs: 2.5, cost: 1000 },
    ],
  },
  {
    date: 'Fri, Apr 18',
    short: 'Apr18',
    entries: [
      { who: 'AK', title: 'feat: JWT refresh token rotation', status: 'done', type: 'commit', hrs: 3, cost: 1500 },
      { who: 'SR', title: 'PR #12 – API layer review', status: 'done', type: 'pr', hrs: 4, cost: 1600 },
      { who: 'PK', title: 'fix: Cart total calculation bug', status: 'done', type: 'commit', hrs: 1.5, cost: 750 },
    ],
  },
  {
    date: 'Thu, Apr 17',
    short: 'Apr17',
    entries: [
      { who: 'AK', title: 'Sprint 4 kickoff & planning', status: 'done', type: 'log', hrs: 2, cost: 1000 },
      { who: 'SR', title: 'Architecture review – frontend state mgmt', status: 'done', type: 'log', hrs: 3, cost: 1200 },
      { who: 'RM', title: 'Initial auth module scaffolding', status: 'done', type: 'commit', hrs: 4, cost: 2000 },
      { who: 'PK', title: 'feat: Product listing with filters', status: 'done', type: 'commit', hrs: 5, cost: 2500 },
    ],
  },
];

const DEMO_REPORT_MEMBERS: Record<string, ReportMember> = {
  AK: { initials: 'AK', name: 'Arjun Kumar', role: 'owner', rate: 500, hrs: 12.5, commits: 6, prs: 3, quality: 88, color: '#4d9cf8', spent: 6250 },
  SR: { initials: 'SR', name: 'Sneha Rao', role: 'lead', rate: 400, hrs: 18, commits: 4, prs: 2, quality: 71, color: '#3ecf8e', spent: 7200 },
  RM: { initials: 'RM', name: 'Rahul Mehta', role: 'dev', rate: 500, hrs: 8, commits: 2, prs: 1, quality: 44, color: '#f26464', spent: 4000 },
  PK: { initials: 'PK', name: 'Priya Kapoor', role: 'dev', rate: 500, hrs: 4, commits: 3, prs: 2, quality: 91, color: '#f5a623', spent: 2000 },
};

const DEMO_PROJECT_REPORTS: Record<string, ProjectReport> = {
  'backend-api': {
    id: 'backend-api',
    name: 'Backend API',
    color: '#3ecf8e',
    budget: 20000,
    spent: 12400,
    complete: 62,
    deadline: 'May 15',
    status: 'on track',
    members: ['AK', 'SR'],
    tasks: { done: 18, pending: 7, total: 25 },
    timeline: [
      { day: 'Apr 25', who: 'AK', text: 'Rate limiting implemented & merged', color: '#3ecf8e' },
      { day: 'Apr 24', who: 'AK', text: 'Dashboard API refactor merged', color: '#3ecf8e' },
      { day: 'Apr 21', who: 'AK', text: 'Race condition hotfix deployed', color: '#f26464' },
      { day: 'Apr 18', who: 'AK', text: 'JWT refresh token rotation done', color: '#3ecf8e' },
      { day: 'Apr 17', who: 'SR', text: 'Architecture review complete', color: '#4d9cf8' },
    ],
    topPerformer: {
      init: 'AK',
      name: 'Arjun Kumar',
      reason: '6 commits, 3 PRs merged, 0 review rounds needed, avg quality 88/100',
    },
  },
  frontend: {
    id: 'frontend',
    name: 'Frontend',
    color: '#4d9cf8',
    budget: 15000,
    spent: 5700,
    complete: 38,
    deadline: 'May 20',
    status: 'on track',
    members: ['PK'],
    tasks: { done: 9, pending: 5, total: 14 },
    timeline: [
      { day: 'Apr 25', who: 'PK', text: 'Payment gateway PR #14 merged – 91/100', color: '#3ecf8e' },
      { day: 'Apr 24', who: 'PK', text: 'Stripe webhook handler & retry logic', color: '#3ecf8e' },
      { day: 'Apr 18', who: 'PK', text: 'Cart total bug fixed', color: '#f5a623' },
      { day: 'Apr 17', who: 'PK', text: 'Product listing with filters', color: '#3ecf8e' },
    ],
    topPerformer: {
      init: 'PK',
      name: 'Priya Kapoor',
      reason: 'Highest quality score (91/100), all PRs merged on first review, clean code with zero critical bugs',
    },
  },
  'auth-service-v2': {
    id: 'auth-service-v2',
    name: 'Auth Service v2',
    color: '#f26464',
    budget: 15000,
    spent: 13200,
    complete: 88,
    deadline: 'Apr 30',
    status: 'at risk',
    members: ['SR', 'RM'],
    tasks: { done: 14, pending: 3, total: 17 },
    timeline: [
      { day: 'Apr 25', who: 'RM', text: 'Auth bug partial fix – session WIP', color: '#f5a623' },
      { day: 'Apr 24', who: 'RM', text: 'PR #13 – 3rd review round requested', color: '#f26464' },
      { day: 'Apr 24', who: 'SR', text: 'API refactor PR raised for review', color: '#f5a623' },
      { day: 'Apr 21', who: 'SR', text: 'Dependency security patches', color: '#4d9cf8' },
      { day: 'Apr 17', who: 'RM', text: 'Auth module scaffolding started', color: '#3ecf8e' },
    ],
    topPerformer: {
      init: 'SR',
      name: 'Sneha Rao',
      reason: 'Led code reviews, identified 6 critical issues in PR #15, maintained team communication. Rahul is at risk and needs support.',
    },
  },
};

const DEMO_INSIGHTS: InsightItem[] = [
  {
    iconBg: 'rgba(245,166,35,.12)',
    iconBorder: 'rgba(245,166,35,.25)',
    iconColor: '#f5a623',
    iconPath: <path d="M8 1l1.7 3.5 3.8.55-2.75 2.68.65 3.77L8 9.75l-3.4 1.78.65-3.77L2.5 5.05l3.8-.55L8 1z" fill="currentColor" />,
    text: <><strong>Priya Kapoor</strong> is the most efficient this sprint — 91/100 quality, all PRs merged on first review, highest commit-to-hour ratio on the team.</>,
  },
  {
    iconBg: 'rgba(242,100,100,.1)',
    iconBorder: 'rgba(242,100,100,.22)',
    iconColor: '#f26464',
    iconPath: <><path d="M8 2L1.5 13h13L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M8 7v3M8 11.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></>,
    text: <><strong>Rahul Mehta</strong> is at risk — 44/100 quality, 3 review rounds on PR #13, session debugging without resolution. Recommend pair programming or task reassignment.</>,
  },
  {
    iconBg: 'rgba(77,156,248,.1)',
    iconBorder: 'rgba(77,156,248,.22)',
    iconColor: '#4d9cf8',
    iconPath: <><path d="M2 12l4-4 3 3 5-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M12 5h2v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></>,
    text: <><strong>Sneha Rao</strong> led the most code reviewed but has 2 pending PRs. As team lead her management overhead is reflected in lower commit count — this is expected.</>,
  },
  {
    iconBg: 'rgba(62,207,142,.1)',
    iconBorder: 'rgba(62,207,142,.22)',
    iconColor: '#3ecf8e',
    iconPath: <><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" /><path d="M5.5 8l2 2 3-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></>,
    text: <>Team velocity is <strong>up 18% vs last sprint</strong>. Budget burn is on track for Backend API and Frontend, but Auth Service needs attention before the deadline.</>,
  },
];

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

function formatCurrency(value: number): string {
  return `₹${Math.round(Math.max(0, value)).toLocaleString('en-IN')}`;
}

function formatHours(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatDayLabel(timestamp: string): { key: string; date: string; short: string } {
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return { key: timestamp, date: timestamp, short: timestamp };
  }
  const weekday = parsed.toLocaleDateString('en-US', { weekday: 'short' });
  const month = parsed.toLocaleDateString('en-US', { month: 'short' });
  const day = parsed.getDate();
  const paddedDay = String(day).padStart(2, '0');
  return {
    key: parsed.toISOString().slice(0, 10),
    date: `${weekday}, ${month} ${day}`,
    short: `${month}${paddedDay}`,
  };
}

function splitReportDate(dateLabel: string): { primary: string; secondary: string } {
  const parts = dateLabel.split(', ');
  return {
    primary: parts[0] || dateLabel,
    secondary: parts[1] || '',
  };
}

function roleLabel(role: string): string {
  if (role === 'owner') return 'Owner';
  if (role === 'lead') return 'Team Lead';
  if (role === 'pm') return 'Product';
  return role ? role[0].toUpperCase() + role.slice(1) : 'Developer';
}

function getVerdict(score: number): { verd: string; verdClass: string } {
  if (score >= 88) return { verd: 'exceptional', verdClass: 'verd vg' };
  if (score >= 75) return { verd: 'high quality', verdClass: 'verd vg' };
  if (score >= 60) return { verd: 'review needed', verdClass: 'verd va' };
  return { verd: 'at risk', verdClass: 'verd vr' };
}

function getBudgetVerdict(score: number): { label: string; className: string } {
  if (score >= 80) return { label: 'high value', className: 'verd vg' };
  if (score >= 60) return { label: 'good value', className: 'verd va' };
  return { label: 'at risk', className: 'verd vr' };
}

function getRankTone(index: number): 'gold' | 'silver' | 'bronze' {
  if (index === 0) return 'gold';
  if (index === 1) return 'silver';
  return 'bronze';
}

function getRankColor(tone: 'gold' | 'silver' | 'bronze'): string {
  if (tone === 'gold') return '#f5a623';
  if (tone === 'silver') return '#9ca3af';
  return '#cd7c3d';
}

function getRankBadgeLabel(index: number): string {
  if (index === 0) return 'Most Efficient';
  if (index === 1) return '2nd Place';
  return '3rd Place';
}

function hexToRgba(hex: string, alpha: number): string {
  if (!hex.startsWith('#')) return hex;
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function deriveEntryStatus(type: string, title: string, quality: number): ReportEntryStatus {
  const lowered = `${type} ${title}`.toLowerCase();
  if (quality < 60 && /(bug|risk|blocked|fail|error|issue)/.test(lowered)) return 'risk';
  if (/(pending|review|wip|debug|todo|follow up)/.test(lowered)) return 'pending';
  if (type === 'pr') return 'pending';
  return 'done';
}

function buildEfficiencyRows(memberMap: Record<string, ReportMember>): EfficiencyRow[] {
  return Object.values(memberMap)
    .map((member) => {
      const commitScore = Math.min((member.commits / 6) * 100, 100);
      const prScore = Math.min((member.prs / 3) * 100, 100);
      const hoursScore = Math.min((member.hrs / 18) * 100, 100);
      const score = Math.round(member.quality * 0.4 + commitScore * 0.25 + prScore * 0.2 + hoursScore * 0.15);
      const verdict = getVerdict(score);
      return {
        initials: member.initials,
        name: member.name,
        role: member.role,
        quality: member.quality,
        commits: member.commits,
        prs: member.prs,
        hours: member.hrs,
        hrsRatio: `${(member.hrs / 18).toFixed(1)}x`,
        score,
        verdClass: verdict.verdClass,
        verd: verdict.verd,
        color: member.color,
        spent: member.spent,
        hourlyRate: member.rate,
        rank: 0,
      } as EfficiencyRow;
    })
    .sort((left, right) => right.score - left.score)
    .map((row, index) => ({
      ...row,
      rank: index + 1,
    }));
}

function buildReportMembersFromReal(
  members: Array<{ initials: string; name: string; role: string; rate: number; hoursThisWeek: number; totalSpent: number; qualityScore: number; commits: number; prsOpen: number }>,
  commitCounts: Map<string, number>,
  prCounts: Map<string, number>,
  latestQualityByInitials: Map<string, number>,
): Record<string, ReportMember> {
  const map: Record<string, ReportMember> = {};
  members.forEach((member, index) => {
    const initials = String(member.initials || '').toUpperCase();
    if (!initials) return;
    map[initials] = {
      initials,
      name: member.name,
      role: member.role,
      rate: Math.max(0, Number(member.rate || 0)),
      hrs: Math.max(0, Number(member.hoursThisWeek || 0)),
      commits: commitCounts.get(initials) || Math.max(0, Number(member.commits || 0)),
      prs: prCounts.get(initials) || Math.max(0, Number(member.prsOpen || 0)),
      quality: clamp(latestQualityByInitials.get(initials) ?? Math.round(Number(member.qualityScore || 0)), 0, 100),
      color: REPORT_MEMBER_COLORS[index % REPORT_MEMBER_COLORS.length],
      spent: Math.max(0, Number(member.totalSpent || 0)),
    };
  });
  return map;
}

function buildRealReportDays(args: {
  activities: LiveActivityItem[];
  commits: LiveCommitItem[];
  memberMap: Record<string, ReportMember>;
  range: RepRange;
}): ReportDay[] {
  const groups = new Map<string, ReportDay>();
  const dedupe = new Set<string>();

  const pushEntry = (timestamp: string, entry: ReportEntry) => {
    const dayMeta = formatDayLabel(timestamp);
    if (!groups.has(dayMeta.key)) {
      groups.set(dayMeta.key, { date: dayMeta.date, short: dayMeta.short, entries: [] });
    }
    groups.get(dayMeta.key)?.entries.push(entry);
  };

  args.activities.forEach((activity) => {
    const initials = String(activity.memberInitials || '').toUpperCase();
    if (!initials) return;
    const member = args.memberMap[initials];
    const title = String(activity.detail || activity.action || 'Workspace update').trim() || 'Workspace update';
    const key = `${activity.timestamp}:${initials}:${title}`;
    if (dedupe.has(key)) return;
    dedupe.add(key);
    const rate = member?.rate || 0;
    const rawCost = Math.max(0, Number(activity.cost || 0));
    const hrs = rawCost > 0 && rate > 0 ? clamp(Math.round((rawCost / rate) * 10) / 10, 0.5, 8) : activity.type === 'commit' || activity.type === 'pr' ? 1.5 : 1;
    pushEntry(activity.timestamp, {
      who: initials,
      title,
      status: deriveEntryStatus(String(activity.type || ''), title, member?.quality ?? 72),
      type: String(activity.type || 'log'),
      hrs,
      cost: rawCost || Math.round(hrs * rate),
    });
  });

  args.commits.forEach((commit) => {
    const initials = String(commit.memberInitials || '').toUpperCase();
    if (!initials) return;
    const member = args.memberMap[initials];
    const title = String(commit.message || 'Commit pushed').trim() || 'Commit pushed';
    const key = `${commit.timestamp}:${initials}:${title}`;
    if (dedupe.has(key)) return;
    dedupe.add(key);
    const rate = member?.rate || 0;
    pushEntry(commit.timestamp, {
      who: initials,
      title,
      status: 'done',
      type: 'commit',
      hrs: rate > 0 ? 1.5 : 1,
      cost: rate > 0 ? Math.round(rate * 1.5) : 0,
    });
  });

  return Array.from(groups.entries())
    .sort((left, right) => right[0].localeCompare(left[0]))
    .slice(0, REPORT_RANGE_LIMITS[args.range])
    .map(([, day]) => day);
}

function buildLiveProjectReport(
  project: Project,
  efficiencyRows: EfficiencyRow[],
  reportDays: ReportDay[],
): ProjectReport {
  const memberIds = new Set(project.members);
  const projectRows = efficiencyRows.filter((row) => memberIds.has(row.initials));
  const fallbackRows = projectRows.length > 0 ? projectRows : efficiencyRows.slice(0, 3);
  const completion = clamp(Math.round(project.completionPct || 0), 0, 100);
  const totalTasks = Math.max(12, fallbackRows.length * 6 || 12);
  const done = Math.round((completion / 100) * totalTasks);
  const pending = Math.max(0, totalTasks - done);
  const timeline = reportDays
    .flatMap((day) =>
      day.entries
        .filter((entry) => memberIds.size === 0 || memberIds.has(entry.who))
        .map((entry) => ({
          day: day.date.replace(',', ''),
          who: entry.who,
          text: entry.title,
          color: entry.status === 'done' ? '#3ecf8e' : entry.status === 'pending' ? '#f5a623' : '#f26464',
        })),
    )
    .slice(0, 5);
  const topPerformer = fallbackRows[0] || null;
  const budgetUsage = project.budget > 0 ? project.spent / project.budget : 0;
  const status = completion >= 100 ? 'delivered' : budgetUsage >= 0.9 ? 'at risk' : completion >= 60 ? 'on track' : 'building';
  const topReason = topPerformer
    ? `${topPerformer.score}/100 efficiency, ${topPerformer.commits} commit${topPerformer.commits === 1 ? '' : 's'}, ${topPerformer.prs} PR signal${topPerformer.prs === 1 ? '' : 's'}, and ${topPerformer.quality}/100 quality.`
    : 'Most efficient contributor will appear once project telemetry accumulates.';

  return {
    id: project.id,
    name: project.name,
    color: project.color || '#4d9cf8',
    budget: Math.max(0, Number(project.budget || 0)),
    spent: Math.max(0, Number(project.spent || 0)),
    complete: completion,
    deadline: project.deadline || 'TBD',
    status,
    members: fallbackRows.map((row) => row.initials),
    tasks: { done, pending, total: done + pending },
    timeline: timeline.length > 0 ? timeline : [{ day: 'Today', who: fallbackRows[0]?.initials || 'TM', text: 'Project telemetry will populate here as the team ships work.', color: '#4d9cf8' }],
    topPerformer: {
      init: topPerformer?.initials || 'TM',
      name: topPerformer?.name || 'Team member',
      reason: topReason,
    },
  };
}

function MedalIcon({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="none">
      <path d="M10 2l1.8 3.6 4 .58-2.9 2.82.68 3.98L10 11.1 6.42 12.98l.68-3.98L4.2 6.18l4-.58L10 2z" fill={color} stroke={`${color}66`} strokeWidth="0.5" />
    </svg>
  );
}

function EfficiencyLabelIcon({ kind }: { kind: 'quality' | 'commits' | 'prs' | 'hours' }) {
  if (kind === 'quality') {
    return <svg viewBox="0 0 12 12" fill="none"><polyline points="1,9 4,5.5 6.5,7 9,3 11,4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  }
  if (kind === 'commits') {
    return <svg viewBox="0 0 12 12" fill="none"><circle cx="6" cy="4" r="2" stroke="currentColor" strokeWidth="1.2" /><path d="M6 6v4M3.5 9.5h5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" /></svg>;
  }
  if (kind === 'prs') {
    return <svg viewBox="0 0 12 12" fill="none"><path d="M3 2v8M3 4l3-2M9 10V4M9 8l2 2-2 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  }
  return <svg viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" /><path d="M6 3.5V6l1.5 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>;
}

export default function ReportsPage() {
  const { showToast, projects, isReal, members, budget, teamSlug, theme } = useTeamDashboard();
  const [mounted, setMounted] = useState(false);
  const [retroContent, setRetroContent] = useState<string | null>(null);
  const [retroLoading, setRetroLoading] = useState(false);

  const triggerRetrospective = async () => {
    if (retroLoading) return;
    setRetroLoading(true);
    showToast('Scrudix AI', 'Generating sprint retrospective summary...', 'info');

    try {
      const configuredBase = import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL.replace(/\/$/, '') : '';
      const base = configuredBase || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001/api' : '/api');
      const aiChatUrl = `${base}/ai/chat`;

      const response = await fetch(aiChatUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('scrudix_token') ? { 'Authorization': `Bearer ${localStorage.getItem('scrudix_token')}` } : {})
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `Analyze the current project "${projectReport?.name || 'Sprint'}" and generate a sprint retrospective. Highlight what went well, budget impact, velocity bottlenecks, and developer contributions. Keep it concise (3-4 bullet points per section).` }],
          systemPrompt: "You are Scrudix AI, the team intelligence assistant. Generate a professional sprint retrospective summary in markdown. Be specific and data-driven."
        }),
      });

      if (!response.ok) throw new Error();
      const json = await response.json();
      const text = json?.data?.response || json?.response || "Failed to generate summary.";
      setRetroContent(text);
      showToast('Scrudix AI', 'Retrospective report ready!', 'success');
    } catch {
      showToast('Scrudix AI', 'Failed to generate retrospective summary.', 'error');
    } finally {
      setRetroLoading(false);
    }
  };

  const exportPDF = () => {
    window.print();
  };
  const [activeTab, setActiveTab] = useState<RepTab>('daily');
  const [range, setRange] = useState<RepRange>('week');
  const [selectedProject, setSelectedProject] = useState('');
  const [liveActivity, setLiveActivity] = useState<LiveActivityItem[]>([]);
  const [liveCommits, setLiveCommits] = useState<LiveCommitItem[]>([]);
  const [liveQuality, setLiveQuality] = useState<LiveQualityItem[]>([]);
  const [telemetryError, setTelemetryError] = useState('');
  const [isTelemetryLoading, setIsTelemetryLoading] = useState(false);
  const revealClass = mounted ? ' in' : '';

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setMounted(true), 30);
    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!isReal) {
      setLiveActivity([]);
      setLiveCommits([]);
      setLiveQuality([]);
      setTelemetryError('');
      setIsTelemetryLoading(false);
      return;
    }

    let cancelled = false;

    async function loadTelemetry() {
      setIsTelemetryLoading(true);
      try {
        const [activityRes, commitsRes, qualityRes] = await Promise.all([
          apiGet<{ data?: LiveActivityItem[] }>(`/${teamSlug}/activity?limit=40`),
          apiGet<{ data?: LiveCommitItem[] }>(`/${teamSlug}/commits?limit=40`),
          apiGet<{ data?: LiveQualityItem[] }>(`/${teamSlug}/quality`),
        ]);
        if (cancelled) return;
        setLiveActivity(Array.isArray(activityRes?.data) ? activityRes.data : []);
        setLiveCommits(Array.isArray(commitsRes?.data) ? commitsRes.data : []);
        setLiveQuality(Array.isArray(qualityRes?.data) ? qualityRes.data : []);
        setTelemetryError('');
      } catch (error) {
        if (cancelled) return;
        setLiveActivity([]);
        setLiveCommits([]);
        setLiveQuality([]);
        setTelemetryError(error instanceof Error ? error.message : 'Unable to load live report telemetry.');
      } finally {
        if (!cancelled) {
          setIsTelemetryLoading(false);
        }
      }
    }

    void loadTelemetry();
    const intervalId = window.setInterval(() => {
      void loadTelemetry();
    }, 45000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isReal, teamSlug]);

  useEffect(() => {
    if (!projects.length) return;
    if (!selectedProject || !projects.some((project) => project.id === selectedProject)) {
      setSelectedProject(projects[0].id);
    }
  }, [projects, selectedProject]);

  const selectedProj = useMemo(() => {
    if (!projects.length) return null;
    return projects.find((project) => project.id === selectedProject) || projects[0];
  }, [projects, selectedProject]);

  const commitCounts = useMemo(() => {
    const counts = new Map<string, number>();
    liveCommits.forEach((commit) => {
      const initials = String(commit.memberInitials || '').toUpperCase();
      if (!initials) return;
      counts.set(initials, (counts.get(initials) || 0) + 1);
    });
    return counts;
  }, [liveCommits]);

  const prCounts = useMemo(() => {
    const counts = new Map<string, number>();
    liveActivity.forEach((activity) => {
      const initials = String(activity.memberInitials || '').toUpperCase();
      const lowered = `${activity.action} ${activity.detail}`.toLowerCase();
      const isPr = activity.type === 'pr' || /pr\s?#|pull request|review/.test(lowered);
      if (!initials || !isPr) return;
      counts.set(initials, (counts.get(initials) || 0) + 1);
    });
    return counts;
  }, [liveActivity]);

  const latestQualityByInitials = useMemo(() => {
    const latest = new Map<string, number>();
    liveQuality.forEach((record) => {
      const initials = String(record.memberInitials || '').toUpperCase();
      if (!initials || latest.has(initials)) return;
      latest.set(initials, Math.round(Number(record.score || 0)));
    });
    return latest;
  }, [liveQuality]);

  const reportMemberMap = useMemo(() => {
    if (!isReal) return DEMO_REPORT_MEMBERS;
    return buildReportMembersFromReal(members, commitCounts, prCounts, latestQualityByInitials);
  }, [commitCounts, isReal, latestQualityByInitials, members, prCounts]);

  const efficiencyRows = useMemo(() => buildEfficiencyRows(reportMemberMap), [reportMemberMap]);

  const reportDays = useMemo(() => {
    if (!isReal) return DEMO_REPORT_DAYS.slice(0, REPORT_RANGE_LIMITS[range]);
    return buildRealReportDays({
      activities: liveActivity,
      commits: liveCommits,
      memberMap: reportMemberMap,
      range,
    });
  }, [isReal, liveActivity, liveCommits, range, reportMemberMap]);

  const reportEntries = useMemo(() => reportDays.flatMap((day) => day.entries), [reportDays]);

  const dailyTotals = useMemo(() => {
    const done = reportEntries.filter((entry) => entry.status === 'done').length;
    const pending = reportEntries.filter((entry) => entry.status !== 'done').length;
    const hours = reportEntries.reduce((sum, entry) => sum + entry.hrs, 0);
    const cost = reportEntries.reduce((sum, entry) => sum + entry.cost, 0);
    const activeDays = reportDays.length;
    return { done, pending, hours, cost, activeDays };
  }, [reportDays, reportEntries]);

  const effectiveTotalBudget = useMemo(() => {
    if (!isReal) return 50000;
    if (budget) return Math.max(0, Number(budget.total || 0));
    return projects.reduce((sum, project) => sum + Math.max(0, Number(project.budget || 0)), 0);
  }, [budget, isReal, projects]);

  const effectiveSpentBudget = useMemo(() => {
    if (!isReal) return Object.values(DEMO_REPORT_MEMBERS).reduce((sum, member) => sum + member.spent, 0);
    if (budget) return Math.max(0, Number(budget.spent || 0));
    return Object.values(reportMemberMap).reduce((sum, member) => sum + member.spent, 0);
  }, [budget, isReal, reportMemberMap]);

  const effectiveRemainingBudget = useMemo(() => {
    if (!isReal) return 50000 - effectiveSpentBudget;
    if (budget) return Math.max(0, Number(budget.remaining || 0));
    return Math.max(0, effectiveTotalBudget - effectiveSpentBudget);
  }, [budget, effectiveSpentBudget, effectiveTotalBudget, isReal]);

  const memberRows = useMemo(() => Object.values(reportMemberMap), [reportMemberMap]);

  const donutSegments = useMemo(() => {
    const radius = 55;
    const circumference = 2 * Math.PI * radius;
    const total = Math.max(1, memberRows.reduce((sum, member) => sum + member.spent, 0));
    let offset = 0;
    return memberRows.map((member) => {
      const fraction = member.spent / total;
      const dash = fraction * circumference;
      const segment = {
        member,
        circumference,
        dash,
        offset: offset * circumference,
      };
      offset += fraction;
      return segment;
    });
  }, [memberRows]);

  const spendTrend = useMemo(() => {
    return [...reportDays]
      .reverse()
      .slice(-7)
      .map((day) => ({
        label: splitReportDate(day.date).primary,
        value: day.entries.reduce((sum, entry) => sum + entry.cost, 0),
      }));
  }, [reportDays]);

  const heatmapDays = useMemo(() => [...reportDays].reverse(), [reportDays]);

  const riskProject = useMemo(() => {
    const candidates = projects
      .filter((project) => Number(project.budget || 0) > 0)
      .map((project) => ({
        project,
        pct: Number(project.spent || 0) / Math.max(1, Number(project.budget || 0)),
      }))
      .sort((left, right) => right.pct - left.pct);
    return candidates[0] || null;
  }, [projects]);

  const insights = useMemo(() => {
    if (!isReal) return DEMO_INSIGHTS;
    const best = efficiencyRows[0] || null;
    const risk = efficiencyRows[efficiencyRows.length - 1] || null;
    const linesChanged = liveCommits.reduce(
      (sum, commit) => sum + Math.max(0, Number(commit.linesAdded || 0)) + Math.max(0, Number(commit.linesRemoved || 0)),
      0,
    );
    const activeProjects = projects.filter((project) => project.status === 'active' || project.status === 'review').length;

    const insightItems: Array<InsightItem | null> = [
      best
        ? {
            iconBg: 'rgba(245,166,35,.12)',
            iconBorder: 'rgba(245,166,35,.25)',
            iconColor: '#f5a623',
            iconPath: <path d="M8 1l1.7 3.5 3.8.55-2.75 2.68.65 3.77L8 9.75l-3.4 1.78.65-3.77L2.5 5.05l3.8-.55L8 1z" fill="currentColor" />,
            text: <><strong>{best.name}</strong> is leading at {best.score}/100 with {best.commits} commit{best.commits === 1 ? '' : 's'} and {best.quality}/100 quality.</>,
          }
        : null,
      risk
        ? {
            iconBg: 'rgba(242,100,100,.1)',
            iconBorder: 'rgba(242,100,100,.22)',
            iconColor: '#f26464',
            iconPath: <><path d="M8 2L1.5 13h13L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M8 7v3M8 11.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></>,
            text: <><strong>{risk.name}</strong> needs support at {risk.score}/100. Current signal mix is {risk.commits} commit{risk.commits === 1 ? '' : 's'}, {risk.prs} PR signal{risk.prs === 1 ? '' : 's'}, and {risk.hours.toFixed(1)}h logged.</>,
          }
        : null,
      {
        iconBg: 'rgba(77,156,248,.1)',
        iconBorder: 'rgba(77,156,248,.22)',
        iconColor: '#4d9cf8',
        iconPath: <><path d="M2 12l4-4 3 3 5-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M12 5h2v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></>,
        text: <>Live telemetry now spans <strong>{dailyTotals.activeDays} tracked day{dailyTotals.activeDays === 1 ? '' : 's'}</strong>, {activeProjects} active project{activeProjects === 1 ? '' : 's'}, and {linesChanged.toLocaleString('en-IN')} lines changed.</>,
      },
      riskProject
        ? {
            iconBg: 'rgba(62,207,142,.1)',
            iconBorder: 'rgba(62,207,142,.22)',
            iconColor: '#3ecf8e',
            iconPath: <><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" /><path d="M5.5 8l2 2 3-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></>,
            text: <>{riskProject.project.name} has used <strong>{Math.round(riskProject.pct * 100)}%</strong> of its allocated budget, which is now the clearest spend signal in the workspace.</>,
          }
        : {
            iconBg: 'rgba(62,207,142,.1)',
            iconBorder: 'rgba(62,207,142,.22)',
            iconColor: '#3ecf8e',
            iconPath: <><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" /><path d="M5.5 8l2 2 3-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></>,
            text: <>Budget health is stable so far. Add more project allocations to unlock stronger spend-at-risk guidance.</>,
          },
    ];

    return insightItems.filter((item): item is InsightItem => item !== null);
  }, [dailyTotals.activeDays, efficiencyRows, isReal, liveCommits, projects, riskProject]);

  const projectReport = useMemo(() => {
    if (!selectedProj) return null;
    if (!isReal) {
      return DEMO_PROJECT_REPORTS[selectedProj.id] || DEMO_PROJECT_REPORTS['backend-api'];
    }
    return buildLiveProjectReport(selectedProj, efficiencyRows, reportDays);
  }, [efficiencyRows, isReal, reportDays, selectedProj]);

  const projectMemberRows = useMemo(() => {
    if (!projectReport) return [];
    const memberSet = new Set(projectReport.members);
    const filtered = efficiencyRows.filter((row) => memberSet.has(row.initials));
    return filtered.length > 0 ? filtered : efficiencyRows.slice(0, 3);
  }, [efficiencyRows, projectReport]);

  const projectBudgetPct = projectReport && projectReport.budget > 0
    ? Math.round((projectReport.spent / projectReport.budget) * 100)
    : 0;

  return (
    <div className="td-reports" data-theme={theme}>
      <div className="ptitle">Reports</div>
      <div className="psub">Daily activity, completion tracking, budget overview &amp; efficiency rankings</div>

      <div className={`rep-range fu${revealClass}`}>
        {(['week', 'month', 'sprint', 'all'] as const).map((nextRange) => (
          <button
            key={nextRange}
            className={`rep-range-btn${range === nextRange ? ' active' : ''}`}
            onClick={() => setRange(nextRange)}
          >
            {nextRange === 'week' ? 'This week' : nextRange === 'month' ? 'This month' : nextRange === 'sprint' ? 'Sprint 4' : 'All time'}
          </button>
        ))}
        <button className="rep-export-btn" onClick={() => showToast('Export', 'Generating PDF report...', 'info')}>
          <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
            <path d="M7 1v8M4 6l3 3 3-3M2 10v1.5A1.5 1.5 0 0 0 3.5 13h7a1.5 1.5 0 0 0 1.5-1.5V10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Export PDF
        </button>
      </div>

      <div className={`rep-tabs fu${revealClass}`} style={{ transitionDelay: '.04s' }}>
        {(['daily', 'budget', 'efficiency', 'complete'] as const).map((tab) => (
          <button key={tab} className={`rep-tab${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab === 'daily' ? 'Daily Activity' : tab === 'budget' ? 'Budget' : tab === 'efficiency' ? 'Efficiency' : 'Project Complete'}
          </button>
        ))}
      </div>

      {isReal && (isTelemetryLoading || telemetryError) ? (
        <div className={`card fu${revealClass}`} style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            {isTelemetryLoading
              ? 'Loading live workspace reports...'
              : `${telemetryError}. Showing the rest of the team dashboard report UI with currently available workspace metrics.`}
          </div>
        </div>
      ) : null}

      <div className={`rep-section${activeTab === 'daily' ? ' active' : ''}`}>
        <div className={`g4 mb fu${revealClass}`} style={{ transitionDelay: '.06s' }}>
          <div className="sc tilt"><div className="sc-eye">Tasks completed</div><div className="sc-val" style={{ color: 'var(--green)' }}>{dailyTotals.done}</div><div className="sc-change up">this week</div></div>
          <div className="sc tilt"><div className="sc-eye">In progress</div><div className="sc-val" style={{ color: 'var(--amber)' }}>{dailyTotals.pending}</div><div className="sc-change" style={{ color: 'var(--amber)' }}>pending</div></div>
          <div className="sc tilt"><div className="sc-eye">Total hrs logged</div><div className="sc-val">{formatHours(dailyTotals.hours)}</div><div className="sc-change up">across team</div></div>
          <div className="sc tilt"><div className="sc-eye">Budget used</div><div className="sc-val">{formatCurrency(dailyTotals.cost)}</div><div className="sc-change dn">this range</div></div>
        </div>

        <div className={`card fu mb${revealClass}`} style={{ transitionDelay: '.1s' }}>
          <div className="ch"><span className="ct">Day-by-day activity</span><span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>who did what · completed · pending</span></div>
          {reportDays.length === 0 ? (
            <div style={{ padding: 16, fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
              No activity has been recorded yet for this range.
            </div>
          ) : (
            <div id="rep-dbd-table-wrap">
              <table className="dbd-table">
                <thead>
                  <tr>
                    <th className="dbd-th">Date</th>
                    <th className="dbd-th">Member activity</th>
                    <th className="dbd-th">Completed</th>
                    <th className="dbd-th">Pending / WIP</th>
                    <th className="dbd-th">Hrs logged</th>
                    <th className="dbd-th">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {reportDays.map((day) => {
                    const dateParts = splitReportDate(day.date);
                    const grouped = day.entries.reduce<Record<string, { hrs: number; entries: ReportEntry[] }>>((acc, entry) => {
                      if (!acc[entry.who]) acc[entry.who] = { hrs: 0, entries: [] };
                      acc[entry.who].hrs += entry.hrs;
                      acc[entry.who].entries.push(entry);
                      return acc;
                    }, {});
                    const maxHours = Math.max(1, ...Object.values(grouped).map((value) => value.hrs));
                    const doneEntries = day.entries.filter((entry) => entry.status === 'done');
                    const pendingEntries = day.entries.filter((entry) => entry.status !== 'done');
                    const totalHours = day.entries.reduce((sum, entry) => sum + entry.hrs, 0);
                    const totalCost = day.entries.reduce((sum, entry) => sum + entry.cost, 0);

                    return (
                      <tr key={day.date} className="dbd-tr">
                        <td className="dbd-td">
                          <div style={{ fontWeight: 500 }}>{dateParts.primary}</div>
                          <div className="dbd-date">{dateParts.secondary}</div>
                        </td>
                        <td className="dbd-td">
                          <div className="dbd-mini-bars">
                            {Object.entries(grouped).map(([initials, value]) => {
                              const member = reportMemberMap[initials];
                              const pct = Math.round((value.hrs / maxHours) * 100);
                              return (
                                <div key={`${day.date}-${initials}`} className="dbd-mini-row">
                                  <div className="dbd-mini-av" style={{ background: `${member?.color || '#888'}22`, color: member?.color || '#888' }}>{initials}</div>
                                  <div className="dbd-mini-bar-wrap"><div className="dbd-mini-bar-fill" style={{ width: `${pct}%`, background: member?.color || '#888' }} /></div>
                                  <div className="dbd-mini-label">{formatHours(value.hrs)}h</div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                        <td className="dbd-td">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {doneEntries.length > 0 ? doneEntries.slice(0, 2).map((entry, index) => (
                              <div key={`${day.date}-done-${index}`} className="rep-pill-done">✓ {entry.who}: {entry.title.length > 30 ? `${entry.title.slice(0, 30)}...` : entry.title}</div>
                            )) : <span style={{ fontSize: 10, color: 'var(--text3)' }}>—</span>}
                            {doneEntries.length > 2 ? <div style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>+{doneEntries.length - 2} more</div> : null}
                          </div>
                        </td>
                        <td className="dbd-td">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {pendingEntries.length > 0 ? pendingEntries.slice(0, 2).map((entry, index) => (
                              <div key={`${day.date}-pending-${index}`} className={entry.status === 'risk' ? 'rep-pill-risk' : 'rep-pill-pend'}>{entry.status === 'risk' ? '⚠' : '◷'} {entry.who}: {entry.title.length > 28 ? `${entry.title.slice(0, 28)}...` : entry.title}</div>
                            )) : <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--green)' }}>All clear ✓</span>}
                            {pendingEntries.length > 2 ? <div style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>+{pendingEntries.length - 2} more</div> : null}
                          </div>
                        </td>
                        <td className="dbd-td" style={{ fontWeight: 600 }}>{formatHours(totalHours)}</td>
                        <td className="dbd-td" style={{ color: 'var(--green)', fontWeight: 600 }}>{formatCurrency(totalCost)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={`card fu${revealClass}`} style={{ transitionDelay: '.15s' }}>
          <div className="ch"><span className="ct">Team activity heatmap</span><span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>commits + hours per member per day</span></div>
          <div style={{ padding: 16 }}>
            {memberRows.length === 0 || heatmapDays.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>Heatmap will appear once activity is recorded.</div>
            ) : (
              <>
                <div className="heat-grid" style={{ gridTemplateColumns: `80px repeat(${heatmapDays.length}, minmax(0, 1fr))`, marginBottom: 6 }}>
                  <div className="heat-label">Member</div>
                  {heatmapDays.map((day) => <div key={`head-${day.date}`} className="heat-col-head">{day.date.replace(',', '')}</div>)}
                </div>
                <div>
                  {memberRows.map((member) => {
                    const hoursByDay = heatmapDays.map((day) => day.entries.filter((entry) => entry.who === member.initials).reduce((sum, entry) => sum + entry.hrs, 0));
                    const maxValue = Math.max(1, ...hoursByDay);
                    return (
                      <div key={member.initials} className="heat-grid" style={{ gridTemplateColumns: `80px repeat(${heatmapDays.length}, minmax(0, 1fr))`, marginBottom: 3 }}>
                        <div className="heat-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }}><div className="dbd-mini-av" style={{ width: 16, height: 16, background: `${member.color}22`, color: member.color }}>{member.initials}</div></div>
                        {heatmapDays.map((day, index) => {
                          const value = hoursByDay[index];
                          const intensity = value / maxValue;
                          const background = value === 0 ? 'var(--bg3)' : hexToRgba(member.color, 0.15 + intensity * 0.7);
                          return (
                            <div key={`${member.initials}-${day.date}`} className="heat-cell" style={{ background }}>
                              <div className="heat-cell-tip">{member.name}: {formatHours(value)}h on {day.date.replace(',', '')}</div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={`rep-section${activeTab === 'budget' ? ' active' : ''}`}>
        <div className={`g3 mb fu${revealClass}`} style={{ transitionDelay: '.06s' }}>
          <div className="sc tilt"><div className="sc-eye">Total budget</div><div className="sc-val">{formatCurrency(effectiveTotalBudget)}</div><div className="sc-change" style={{ color: 'var(--text3)' }}>allocated</div></div>
          <div className="sc tilt"><div className="sc-eye">Spent</div><div className="sc-val" style={{ color: 'var(--red)' }}>{formatCurrency(effectiveSpentBudget)}</div><div className="sc-change dn">this sprint</div></div>
          <div className="sc tilt"><div className="sc-eye">Remaining</div><div className="sc-val" style={{ color: 'var(--green)' }}>{formatCurrency(effectiveRemainingBudget)}</div><div className="sc-change up">healthy</div></div>
        </div>

        <div className="g2 mb">
          <div className={`card fu${revealClass}`} style={{ transitionDelay: '.08s' }}>
            <div className="ch"><span className="ct">Budget split by member</span></div>
            <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', width: 150, height: 150, flexShrink: 0 }}>
                <svg width="150" height="150" viewBox="0 0 150 150">
                  <circle cx="75" cy="75" r="55" fill="none" stroke="var(--bg3)" strokeWidth="18" />
                  {donutSegments.map((segment) => (
                    <circle
                      key={segment.member.initials}
                      cx="75"
                      cy="75"
                      r="55"
                      fill="none"
                      stroke={segment.member.color}
                      strokeWidth="18"
                      strokeDasharray={`${segment.dash} ${segment.circumference}`}
                      strokeDashoffset={-segment.offset}
                    />
                  ))}
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--text)' }}>
                    {effectiveTotalBudget > 0 ? `${Math.round((effectiveSpentBudget / effectiveTotalBudget) * 100)}%` : '0%'}
                  </div>
                  <div style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>used</div>
                </div>
              </div>
              <div className="rep-legend" style={{ flex: 1, minWidth: 220 }}>
                {memberRows.map((member) => {
                  const pct = effectiveSpentBudget > 0 ? Math.round((member.spent / effectiveSpentBudget) * 100) : 0;
                  return (
                    <div key={member.initials} className="rep-leg-row">
                      <div className="rep-leg-dot" style={{ background: member.color }} />
                      <span className="rep-leg-lbl">{member.name.split(' ')[0]}</span>
                      <span className="rep-leg-pct">{pct}%</span>
                      <span className="rep-leg-val">{formatCurrency(member.spent)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={`card fu${revealClass}`} style={{ transitionDelay: '.12s', gridColumn: 'span 2' }}>
            <div className="ch" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="ct">Sprint 4 Budget Burn-Down</span>
              <span style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>Dashed: Target · Solid: Actual Remaining</span>
            </div>
            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Daily spend trend bar graph */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 12 }}>Daily Spend Volume</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 90, marginBottom: 8 }}>
                  {spendTrend.map((day) => {
                    const maxSpend = Math.max(1, ...spendTrend.map((item) => item.value));
                    const peak = day.value === maxSpend;
                    return <div key={day.label} style={{ flex: 1, borderRadius: '3px 3px 0 0', background: peak ? 'var(--green)' : 'var(--bg3)', minHeight: 3, height: `${(day.value / maxSpend) * 85}px`, transition: 'height 1s cubic-bezier(.16,1,.3,1)' }} title={`${day.label}: ${formatCurrency(day.value)}`} />;
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>
                  {spendTrend.map((day) => <span key={`label-${day.label}`}>{day.label}</span>)}
                </div>
              </div>

              {/* SVG Line Burn-Down */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 12 }}>Remaining Budget Trajectory</div>
                <div style={{ position: 'relative', width: '100%', height: 90 }}>
                  <svg width="100%" height="90" viewBox="0 0 200 90" preserveAspectRatio="none">
                    {/* Ideal burn down diagonal line */}
                    <line x1="10" y1="5" x2="190" y2="85" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="3 3" />
                    
                    {/* Actual burn down line */}
                    <path
                      d={(() => {
                        const totalDays = 7;
                        let currentBudget = effectiveTotalBudget;
                        const points = [[10, 5]]; // Start at Day 0 with full budget
                        
                        spendTrend.forEach((day, index) => {
                          currentBudget = Math.max(0, currentBudget - day.value);
                          const x = 10 + ((index + 1) / totalDays) * 180;
                          // Map y-axis: 5 (max budget) to 85 (0 budget)
                          const y = 85 - (effectiveTotalBudget > 0 ? (currentBudget / effectiveTotalBudget) * 80 : 0);
                          points.push([x, y]);
                        });
                        
                        return 'M ' + points.map(p => p.join(' ')).join(' L ');
                      })()}
                      fill="none"
                      stroke="#4d9cf8"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text3)', marginTop: 8 }}>
                  <span>Day 1</span>
                  <span>Day 4</span>
                  <span>Day 7</span>
                </div>
              </div>
            </div>
            
            <div style={{ padding: '0 16px 16px' }}>
              <div style={{ padding: '10px 12px', background: 'var(--rdim)', border: '1px solid var(--rborder)', borderRadius: 6 }}>
                <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--red)', marginBottom: 2 }}>⚠ {riskProject?.project.name || 'Spend signal'} at risk</div>
                <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text2)' }}>
                  {riskProject ? `${Math.round(riskProject.pct * 100)}% of ${formatCurrency(riskProject.project.budget)} used — ${formatCurrency(Math.max(0, riskProject.project.budget - riskProject.project.spent))} left before delivery` : 'No project budget risk signal yet.'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`card fu${revealClass}`} style={{ transitionDelay: '.16s' }}>
          <div className="ch"><span className="ct">Budget breakdown by member</span></div>
          <div className="qsh" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr' }}>
            <span className="qsth">Member</span><span className="qsth">Rate</span><span className="qsth">Hrs worked</span><span className="qsth">Spent</span><span className="qsth">Efficiency</span><span className="qsth">Verdict</span>
          </div>
          {efficiencyRows.map((row) => {
            const budgetVerdict = getBudgetVerdict(row.score);
            return (
              <div key={row.initials} className="qsr" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr' }}>
                <div className="qsm"><div className="avr avsm" style={{ background: `${row.color}22`, color: row.color }}>{row.initials}</div><div><div className="qsmn">{row.name}</div><div className="qsmr">{roleLabel(row.role).toLowerCase()}</div></div></div>
                <span className="qstd">{formatCurrency(row.hourlyRate)}/hr</span>
                <span className="qstd">{formatHours(row.hours)}h</span>
                <span className="qstd" style={{ color: 'var(--green)' }}>{formatCurrency(row.spent)}</span>
                <span className="qstd" style={{ color: row.score >= 80 ? 'var(--green)' : row.score >= 60 ? 'var(--amber)' : 'var(--red)' }}>{row.score}/100</span>
                <span className={budgetVerdict.className}>{budgetVerdict.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`rep-section${activeTab === 'efficiency' ? ' active' : ''}`}>
        <div className={`eff-trend-header fu${revealClass}`}>
          <div className="eff-formula-pill">
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 9l3-6 2 4 1.5-2.5L11 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
            score = quality × commit velocity × hrs ratio × pr success
          </div>
        </div>

        {efficiencyRows.length === 0 ? (
          <div className={`card fu${revealClass}`} style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>No efficiency data yet. Add members or live telemetry to generate rankings.</div>
          </div>
        ) : (
          <>
            <div className={`eff-podium fu${revealClass}`} style={{ transitionDelay: '.05s' }}>
              {efficiencyRows.slice(0, 3).map((row, index) => {
                const tone = getRankTone(index);
                const rankColor = getRankColor(tone);
                const circumference = 2 * Math.PI * 20;
                const dashOffset = circumference * (1 - row.score / 100);
                return (
                  <div key={row.initials} className={`eff-card ${tone}`}>
                    <div className="eff-header-row">
                      <div>
                        <div className={`eff-medal ${tone}`}><MedalIcon color={rankColor} /></div>
                        <div className="eff-rank">#{index + 1} {tone.toUpperCase()}</div>
                        <div className="eff-name">{row.name}</div>
                        <div className="eff-role">{roleLabel(row.role)} · {formatCurrency(row.hourlyRate)}/hr</div>
                      </div>
                      <div className="eff-ring-wrap">
                        <svg width="54" height="54" viewBox="0 0 54 54">
                          <circle cx="27" cy="27" r="20" fill="none" stroke="var(--bg3)" strokeWidth="6" />
                          <circle cx="27" cy="27" r="20" fill="none" stroke={rankColor} strokeWidth="6" strokeLinecap="round" strokeDasharray={circumference.toFixed(1)} strokeDashoffset={dashOffset.toFixed(1)} />
                        </svg>
                        <div className="eff-ring-center" style={{ color: rankColor }}>{row.score}</div>
                      </div>
                    </div>
                    <div className="eff-score-wrap"><div className="eff-score" style={{ color: rankColor }}>{row.score}</div><div className="eff-score-max">/100</div></div>
                    <div className="eff-bar-outer"><div className="eff-bar-inner" style={{ width: `${row.score}%`, background: rankColor }} /></div>
                    <div className="eff-stats">
                      <div className="eff-stat-row"><span className="eff-stat-lbl"><EfficiencyLabelIcon kind="quality" />Quality</span><span className="eff-stat-val" style={{ color: row.quality >= 80 ? 'var(--green)' : row.quality >= 60 ? 'var(--amber)' : 'var(--red)' }}>{row.quality}/100</span></div>
                      <div className="eff-stat-row"><span className="eff-stat-lbl"><EfficiencyLabelIcon kind="commits" />Commits</span><span className="eff-stat-val">{row.commits}</span></div>
                      <div className="eff-stat-row"><span className="eff-stat-lbl"><EfficiencyLabelIcon kind="prs" />PRs merged</span><span className="eff-stat-val">{row.prs}</span></div>
                      <div className="eff-stat-row"><span className="eff-stat-lbl"><EfficiencyLabelIcon kind="hours" />Hrs logged</span><span className="eff-stat-val">{formatHours(row.hours)}h</span></div>
                    </div>
                    <div className={`eff-badge ${tone}`}>{getRankBadgeLabel(index)}</div>
                  </div>
                );
              })}
            </div>

            <div className={`card fu mb${revealClass}`} style={{ transitionDelay: '.1s' }}>
              <div className="ch"><span className="ct">Detailed efficiency breakdown</span></div>
              <div className="qsh" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1.2fr' }}>
                <span className="qsth">Member</span>
                <span className="qsth">Quality</span>
                <span className="qsth">Commits</span>
                <span className="qsth">PRs merged</span>
                <span className="qsth">Hrs ratio</span>
                <span className="qsth">Score</span>
                <span className="qsth">Verdict</span>
              </div>
              {efficiencyRows.map((row) => (
                <div key={row.initials} className="qsr" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1.2fr' }}>
                  <div className="qsm"><div className="avr avsm" style={{ background: `${row.color}22`, color: row.color }}>{row.initials}</div><div><div className="qsmn">{row.name}</div><div className="qsmr">{row.role}</div></div></div>
                  <span className="qstd" style={{ color: row.quality >= 80 ? 'var(--green)' : row.quality >= 60 ? 'var(--amber)' : 'var(--red)' }}>{row.quality}/100</span>
                  <span className="qstd">{row.commits}</span>
                  <span className="qstd">{row.prs}</span>
                  <span className="qstd">{row.hrsRatio}</span>
                  <span className="qstd" style={{ color: row.score >= 80 ? 'var(--green)' : row.score >= 60 ? 'var(--amber)' : 'var(--red)', fontWeight: 700 }}>{row.score}/100</span>
                  <span className={row.verdClass}>{row.verd}</span>
                </div>
              ))}
            </div>

            <div className={`card fu${revealClass}`} style={{ transitionDelay: '.15s' }}>
              <div className="ch"><span className="ct" style={{ display: 'flex', alignItems: 'center', gap: 7 }}><svg width="13" height="13" viewBox="0 0 14 14" fill="none" style={{ color: 'var(--blue)' }}><path d="M7 1a6 6 0 1 1 0 12A6 6 0 0 1 7 1z" stroke="currentColor" strokeWidth="1.3" /><path d="M7 4v4M7 9.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>Key insights</span></div>
              <div style={{ padding: '14px 16px' }}>
                {insights.map((insight, index) => (
                  <div key={index} className="rep-insight" style={index === insights.length - 1 ? { marginBottom: 0 } : undefined}>
                    <div className="rep-insight-icon-wrap" style={{ background: insight.iconBg, border: `1px solid ${insight.iconBorder}` }}>
                      <svg viewBox="0 0 16 16" fill="none" style={{ color: insight.iconColor }}>{insight.iconPath}</svg>
                    </div>
                    <div className="rep-insight-text">{insight.text}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className={`rep-section${activeTab === 'complete' ? ' active' : ''}`}>
        <div className={`fu${revealClass}`} style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>Project:</span>
          <div className="rep-tabs" style={{ marginBottom: 0 }}>
            {projects.map((project) => (
              <button key={project.id} className={`rep-tab${selectedProject === project.id ? ' active' : ''}`} onClick={() => setSelectedProject(project.id)}>
                {project.name}
              </button>
            ))}
          </div>
        </div>

        {!projectReport ? (
          <div className={`card fu${revealClass}`}><div style={{ padding: 16, fontSize: 12, color: 'var(--text3)' }}>No projects available.</div></div>
        ) : (
          <>
            {projectReport.complete >= 100 ? (
              <div className={`proj-complete-banner fu${revealClass}`}>
                <div className="pcb-icon">🎉</div>
                <div><div className="pcb-title">Project delivered!</div><div className="pcb-sub">{projectReport.name} was completed successfully on time and within budget.</div></div>
                <div className="pcb-confetti">✨🏆✨</div>
              </div>
            ) : null}

            <div className={`g3 mb fu${revealClass}`} style={{ transitionDelay: '.04s' }}>
              <div className="sc tilt"><div className="sc-eye">Completion</div><div className="sc-val" style={{ color: projectReport.complete >= 80 ? 'var(--green)' : projectReport.complete >= 50 ? 'var(--amber)' : 'var(--red)' }}>{projectReport.complete}%</div><div className={`sc-change ${projectReport.complete >= 80 ? 'up' : 'dn'}`}>{projectReport.tasks.done}/{projectReport.tasks.total} tasks</div></div>
              <div className="sc tilt"><div className="sc-eye">Budget used</div><div className="sc-val" style={{ color: projectBudgetPct <= 70 ? 'var(--green)' : projectBudgetPct <= 90 ? 'var(--amber)' : 'var(--red)' }}>{projectBudgetPct}%</div><div className="sc-change dn">{formatCurrency(projectReport.spent)} / {formatCurrency(projectReport.budget)}</div></div>
              <div className="sc tilt"><div className="sc-eye">Deadline</div><div className="sc-val" style={{ fontSize: 16, color: projectReport.status === 'at risk' ? 'var(--red)' : 'var(--text)' }}>{projectReport.deadline}</div><div className="sc-change" style={{ color: projectReport.status === 'at risk' ? 'var(--red)' : 'var(--green)' }}>{projectReport.status}</div></div>
            </div>

            <div className="g2 mb">
              <div className={`card fu${revealClass}`} style={{ transitionDelay: '.08s' }}>
                <div className="ch"><span className="ct">Completion overview</span></div>
                <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
                  <div className="ring-wrap">
                    <svg width="120" height="120" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r="52" fill="none" stroke="var(--bg3)" strokeWidth="10" />
                      <circle cx="60" cy="60" r="52" fill="none" stroke={projectReport.color} strokeWidth="10" strokeDasharray={(2 * Math.PI * 52).toFixed(1)} strokeDashoffset={(2 * Math.PI * 52 * (1 - projectReport.complete / 100)).toFixed(1)} strokeLinecap="round" />
                    </svg>
                    <div className="ring-center"><div className="ring-pct" style={{ color: projectReport.color }}>{projectReport.complete}%</div><div className="ring-sub">complete</div></div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="rep-leg-row" style={{ marginBottom: 8 }}><div className="rep-leg-dot" style={{ background: 'var(--green)' }} /><span className="rep-leg-lbl">Completed</span><span className="rep-leg-val">{projectReport.tasks.done}</span></div>
                    <div className="rep-leg-row" style={{ marginBottom: 8 }}><div className="rep-leg-dot" style={{ background: 'var(--amber)' }} /><span className="rep-leg-lbl">Pending</span><span className="rep-leg-val">{projectReport.tasks.pending}</span></div>
                    <div className="rep-leg-row" style={{ marginBottom: 16 }}><div className="rep-leg-dot" style={{ background: 'var(--bg3)' }} /><span className="rep-leg-lbl">Total tasks</span><span className="rep-leg-val">{projectReport.tasks.total}</span></div>
                    <div className="bbar"><div className="bfill" style={{ width: `${projectReport.complete}%`, background: projectReport.color }} /></div>
                  </div>
                </div>
              </div>

              <div className={`card fu${revealClass}`} style={{ transitionDelay: '.1s' }}>
                <div className="ch"><span className="ct">Member efficiency (this project)</span></div>
                <div style={{ padding: '14px 16px' }}>
                  {projectMemberRows.map((row, index) => (
                    <div key={`${projectReport.id}-${row.initials}`} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: index === projectMemberRows.length - 1 ? 0 : 12 }}>
                      <div className="avr avsm" style={{ background: `${row.color}22`, color: row.color }}>{row.initials}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>{row.name} {index === 0 ? <span className="eff-badge gold" style={{ fontSize: 9, padding: '1px 6px', marginTop: 0 }}>Top</span> : null}</div>
                        <div style={{ background: 'var(--bg3)', borderRadius: 3, height: 5, overflow: 'hidden' }}><div style={{ height: '100%', borderRadius: 3, background: row.color, width: `${row.score}%`, transition: 'width 1.2s cubic-bezier(.16,1,.3,1)' }} /></div>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', color: row.color, minWidth: 40, textAlign: 'right' }}>{row.score}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="g2 mb">
              <div className={`card fu${revealClass}`} style={{ transitionDelay: '.11s' }}>
                <div className="ch" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="ct">Daily Burn-Down & Velocity</span>
                  <span style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>Completed Tasks Trajectory</span>
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ position: 'relative', width: '100%', height: 110 }}>
                    <svg width="100%" height="110" viewBox="0 0 200 110" preserveAspectRatio="none">
                      {/* Grid lines */}
                      <line x1="10" y1="10" x2="190" y2="10" stroke="var(--border)" strokeWidth="0.5" strokeDasharray="2 2" />
                      <line x1="10" y1="50" x2="190" y2="50" stroke="var(--border)" strokeWidth="0.5" strokeDasharray="2 2" />
                      <line x1="10" y1="90" x2="190" y2="90" stroke="var(--border)" strokeWidth="0.5" strokeDasharray="2 2" />

                      {/* Velocity Path */}
                      <path
                        d={(() => {
                          const totalDays = 7;
                          const points: [number, number][] = [];
                          let completedCount = 0;
                          const days = [...reportDays].reverse().slice(-7);
                          days.forEach((day, index) => {
                            const completedToday = day.entries.filter(e => e.status === 'done').length;
                            completedCount += completedToday;
                            const x = 10 + (index / (totalDays - 1)) * 180;
                            const maxTasks = Math.max(10, projectReport.tasks.total);
                            const y = 90 - (completedCount / maxTasks) * 80;
                            points.push([x, y]);
                          });
                          return 'M ' + points.map(p => p.join(' ')).join(' L ');
                        })()}
                        fill="none"
                        stroke={projectReport.color}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--text3)', marginTop: 8 }}>
                    <span>Day 1</span>
                    <span>Day 4</span>
                    <span>Day 7 (Today)</span>
                  </div>
                </div>
              </div>

              {/* AI Retrospective Card */}
              <div className={`card fu${revealClass}`} style={{ transitionDelay: '.15s', display: 'flex', flexDirection: 'column' }}>
                <div className="ch" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="ct">Scrudix AI Retrospective</span>
                  <button className="clink" onClick={triggerRetrospective} disabled={retroLoading} style={{ fontSize: 10 }}>
                    {retroLoading ? 'Analyzing...' : '⚡ Generate'}
                  </button>
                </div>
                <div style={{ padding: 16, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {retroContent ? (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                      <div className="markdown-body" style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.5, maxHeight: 120, overflowY: 'auto', marginBottom: 12, padding: '8px 10px', background: 'var(--bg2)', borderRadius: 6, border: '1px solid var(--border)' }}>
                        {retroContent.split('\n').map((line, i) => (
                          <div key={i} style={{ marginBottom: 4 }}>{line}</div>
                        ))}
                      </div>
                      <button className="btn-p" style={{ width: '100%', fontSize: 10, padding: '6px 12px' }} onClick={exportPDF}>
                        Download Report PDF
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 120, background: 'var(--bg2)', border: '1px dashed var(--border)', borderRadius: 8 }}>
                      <div style={{ fontSize: 24, marginBottom: 8 }}>📊</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', textAlign: 'center', padding: '0 20px' }}>
                        Click generate to let Scrudix AI compile the retrospective and performance report.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={`card mb fu${revealClass}`} style={{ transitionDelay: '.13s' }}>
              <div className="ch"><span className="ct">Activity timeline</span></div>
              <div style={{ padding: 16 }}>
                <div className="tl-wrap">
                  <div className="tl-line" />
                  {projectReport.timeline.map((item, index) => (
                    <div key={`${projectReport.id}-timeline-${index}`} className="tl-item">
                      <div className="tl-dot" style={{ background: item.color }} />
                      <div className="tl-day">{item.day}</div>
                      <div className="tl-entry"><span className="tl-who">{item.who}</span>{item.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={`card fu${revealClass}`} style={{ transitionDelay: '.18s', background: `linear-gradient(135deg, ${hexToRgba(projectReport.color, 0.1)}, ${hexToRgba(projectReport.color, 0.03)})`, borderColor: `${projectReport.color}44` }}>
              <div className="ch" style={{ borderBottomColor: `${projectReport.color}22` }}><span className="ct">🏆 Most efficient contributor</span><span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: projectReport.color }}>post-project analysis</span></div>
              <div style={{ padding: '18px 20px', display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: `${projectReport.color}22`, border: `2px solid ${projectReport.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, fontFamily: 'var(--mono)', color: projectReport.color, flexShrink: 0 }}>{projectReport.topPerformer.init}</div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', letterSpacing: '-.4px', marginBottom: 3 }}>{projectReport.topPerformer.name}</div>
                  <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)', marginBottom: 10 }}>{projectMemberRows[0]?.score || '—'}/100 efficiency score · {projectMemberRows[0] ? `${formatHours(projectMemberRows[0].hours)}h logged · ${formatCurrency(projectMemberRows[0].spent)} cost` : 'project score pending'}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6 }}>{projectReport.topPerformer.reason}</div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}